import { type FetchResult } from "@apollo/client";
import { type FC, useCallback } from "react";
import { Form, type InjectedFormProps, SubmissionError, reduxForm } from "redux-form";

import { DialogButtonsWithErrorCallout, HStack, InfoToggletip } from "widgets";

import InputField, { InputReduxFormField } from "../../core/redux-form/InputField.js";
import { FIELD_FLAG_COLOR, FIELD_FLAG_DESCRIPTION, FIELD_FLAG_NAME, FIELD_PROJECT_FLAGS } from "./_mod.js";
import { type UpdateRegionFlagMutation } from "./regionFlag.gql.js";
import { simpleFieldValidation } from "@/app-utils/form.ts";
import { type InputRegionFlagUpdateInput } from "@/graphql.ts";
import { MAX_LENGTH } from "@/settings/settings.js";
import ColorPickerField, { ColorPickerReduxFormField } from "@/ui-modules/core/redux-form/ColorPickerField.tsx";
import { ProjectTagInputReduxFormField } from "@/ui-modules/core/redux-form/ProjectTagInputField.tsx";

export interface IEditFlagFormProps {
  onCancel: () => void;
  onSaveFlagEditing: (flagUpdateInput: InputRegionFlagUpdateInput) => Promise<FetchResult<UpdateRegionFlagMutation>>;
  projectId: number;
}

const EditFlagForm: FC<IEditFlagFormProps & InjectedFormProps<any, any>> = (props) => {
  const handleSubmitData = useCallback(
    (values) => {
      const description: string = values[FIELD_FLAG_DESCRIPTION];
      const name: string = values[FIELD_FLAG_NAME];
      const color: string = values[FIELD_FLAG_COLOR];
      const itemTagIds: number[] = values[FIELD_PROJECT_FLAGS];

      const flagUpdateInput: InputRegionFlagUpdateInput = {
        description: description?.trim(),
        name: name?.trim(),
        color: color,
        itemTagIds: itemTagIds,
      };
      return props.onSaveFlagEditing(flagUpdateInput).catch((error) => {
        throw new SubmissionError({ _error: error.message });
      });
    },
    [props],
  );

  return (
    <Form>
      <InputReduxFormField
        componentClass="input"
        name={FIELD_FLAG_NAME}
        label="Name"
        placeholder={`Optional name`}
        maxLength={MAX_LENGTH.FLAG.NAME}
        component={InputField}
        validate={simpleFieldValidation}
      />

      <InputReduxFormField
        componentClass="textarea"
        name={FIELD_FLAG_DESCRIPTION}
        label="Description"
        placeholder="Optional description"
        maxLength={MAX_LENGTH.FLAG.DESCRIPTION}
        component={InputField}
      />

      <ProjectTagInputReduxFormField name={FIELD_PROJECT_FLAGS} label="Tags" projectId={props.projectId} />

      <ColorPickerReduxFormField
        name={FIELD_FLAG_COLOR}
        component={ColorPickerField}
        title={
          <HStack>
            Color
            <InfoToggletip
              content={`To use this color in the Genome Viewer, select "Flag Color" in the  track configuration`}
            />
          </HStack>
        }
      />

      <DialogButtonsWithErrorCallout
        mainButton={{
          label: "Save",
          isLoading: props.submitting,
          isDisabled: props.submitting,
          onPress: props.handleSubmit(handleSubmitData),
        }}
        closeFn={props.onCancel}
        errorCallout={{ message: props.error }}
      />
    </Form>
  );
};

const EditFlagFormDialog = reduxForm<{}, IEditFlagFormProps>({})(EditFlagForm);

export { EditFlagFormDialog };
